import React, {useReducer, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import {actions as routerActions} from 'redux-router5';
import {
  Button,
  Divider,
  Form,
  Grid,
  Header,
  Radio,
  Segment,
} from 'semantic-ui-react';

import {
  addressActions,
  processorActions,
  paymentActions as actions,
} from 'actions';
import * as alerts from 'actions/alertActions';
import {isNumber} from 'helpers/formValidation';
import {isPaymentsApp, toFixed, validEmail} from 'helpers';
import {PaymentMethods, ProcessorTypes} from 'helpers/constants';

import {ReceiptForm} from 'Common/Form';
import {Loading} from 'Common/Structure';
import {CurrencyValue} from 'Common/Format';

import PayPal from './PayPal';

const PaymentForm = props => {
  const guestAccess = isPaymentsApp();

  const [state, setState] = useReducer(
    (state, newState) => ({...state, ...newState}),
    {
      amount:
        props.router.route.params.amount !== undefined
          ? toFixed(props.router.route.params.amount, 2)
          : toFixed(props.balanceDue, 2),
      customerEmailList: [],
      fullAmount:
        props.router.route.params.fullAmount !== undefined
          ? props.router.route.params.fullAmount === 'true'
          : true,
      loadingEmailList: false,
      receiptEmails:
        props.router.route.params.receiptEmails !== undefined
          ? props.router.route.params.receiptEmails
          : [],
      sendReceipt:
        props.router.route.params.sendReceipt !== undefined
          ? props.router.route.params.sendReceipt === 'true'
          : false,
    },
  );

  useEffect(
    () => {
      setState({
        fullAmount: true,
        receiptEmails: [],
        sendReceipt: false,
      });
    },
    [props.router.route.params.id],
  );

  useEffect(
    () => {
      setState({
        amount: props.balanceDue,
      });
    },
    [props.balanceDue],
  );

  const [payPalEnabled, setPayPalEnabled] = useState(
    props.paymentMethod === PaymentMethods.PayPal,
  );
  const [bankEnabled, setBankEnabled] = useState(false);
  const [creditCardEnabled, setCreditCardEnabled] = useState(false);
  const obj = props.invoice ? props.invoice : props.estimate;

  useEffect(() => {
    if (guestAccess) {
      loadProcessors();
    }
  }, []);

  const [loading, setLoading] = useState(false);

  const loadProcessors = async () => {
    setLoading(true);
    const results = await props.getPaymentProcessors();
    results.forEach(p => {
      if (p.enabled) {
        if (p.name === ProcessorTypes.PayPal) {
          setPayPalEnabled(true);
        } else if (p.name === ProcessorTypes.WePay) {
          setBankEnabled(true);
          setCreditCardEnabled(true);
        } else {
          setCreditCardEnabled(true);
        }
      }
    });
    setLoading(false);
  };

  const handleAmountChange = (e, {value}) => {
    setState({fullAmount: value === 'full'});

    if (value === 'full') {
      setState({amount: props.balanceDue});
    }
  };

  const handleNextClick = async (method = null) => {
    if (state.sendReceipt) {
      if (state.receiptEmails.length === 0) {
        props.errorAlert(
          'To send a receipt, please include at least one email address.',
        );
        return;
      }
      const invalid = state.receiptEmails.find(x => !validEmail(x));
      if (invalid) {
        props.errorAlert(`${invalid} is not a valid email address`);
        return;
      }
    }

    if (!state.amount || !isNumber(state.amount)) {
      props.errorAlert('The Amount only supports positive numbers.');
      return;
    }

    if (Number(state.amount) > props.balanceDue) {
      props.errorAlert('The Amount cannot exceed the Balance Due.');
      return;
    }

    const type = props.invoice ? 'invoice' : 'estimate';

    props.navigateTo('collectpayment', {
      address1: props[type].address1 || '',
      address2: props[type].address2 || '',
      amount: Number(state.amount.toString().replace(/\,/g, '')),
      city: props[type].city || '',
      countryId: props[type].countryId,
      customerId: props.invoice
        ? props.invoice.customerId
        : props.estimate.customerId,
      email: props[type].email,
      estimateId: (props.estimate && props.estimate.estimateId) || null,
      estimateNumber: (props.estimate && props.estimate.estimateNumber) || null,
      firstName: props[type].firstName,
      fullAmount: state.fullAmount,
      invoiceId: (props.invoice && props.invoice.invoiceId) || null,
      invoiceNumber: (props.invoice && props.invoice.invoiceNumber) || null,
      lastName: props[type].lastName,
      paymentMethod: method || props.router.route.params.method,
      receiptEmails: state.receiptEmails,
      sendReceipt: state.sendReceipt,
      state: props[type].state || '',
      zip: props[type].zip || '',
    });
  };

  if (loading) return <Loading />;

  if (guestAccess && !creditCardEnabled && !payPalEnabled) return null;

  return (
    <Segment>
      <Header as="h2" style={{marginBottom: 15}}>
        Payment Information
      </Header>

      {(!guestAccess || bankEnabled || creditCardEnabled) && (
        <Grid>
          <Grid.Column width={8}>
            <Form.Field>
              <Radio
                checked={state.fullAmount}
                label="Pay full amount"
                name="amountGroup"
                onChange={handleAmountChange}
                value="full"
              />
            </Form.Field>
          </Grid.Column>

          <Grid.Column width={8}>
            <Form.Field>
              <CurrencyValue value={props.balanceDue} />
            </Form.Field>
          </Grid.Column>

          <Grid.Column width={8}>
            <Form.Field>
              <Radio
                checked={!state.fullAmount}
                label="Pay partial amount"
                name="amountGroup"
                onChange={handleAmountChange}
                value="partial"
              />
            </Form.Field>
          </Grid.Column>

          <Grid.Column width={8}>
            <Form.Field>
              <Form.Input
                disabled={state.fullAmount}
                fluid
                onChange={(e, {value}) => setState({amount: value})}
                value={state.amount}
              />
            </Form.Field>
          </Grid.Column>

          <Grid.Column width={16}>
            <ReceiptForm
              customerId={obj.serviceLocationId || obj.customerId}
              forceOpen={props.router.route.params.sendReceipt && !guestAccess}
              guestAccess={guestAccess}
              receiptEmails={props.router.route.params.receiptEmails}
              ticketId={obj.ticketId}
              updateReceipts={(value, sendReceipt) =>
                setState({sendReceipt, receiptEmails: value})
              }
              value={state.receiptEmails}
            />
          </Grid.Column>
        </Grid>
      )}

      <Divider style={{margin: '20px 0'}} />

      <div style={{textAlign: 'center'}}>
        {((guestAccess && creditCardEnabled) ||
          (!guestAccess && props.paymentMethod !== PaymentMethods.PayPal)) && (
          <Button
            content={guestAccess ? 'Pay with Credit Card' : 'Next'}
            onClick={() => handleNextClick()}
            primary
            type="button"
          />
        )}

        {bankEnabled && (
          <>
            {guestAccess && <Divider horizontal>Or</Divider>}
            <Button
              content="Pay with Bank (ACH)"
              onClick={() => {
                handleNextClick(PaymentMethods.BankACH);
              }}
              primary
              type="button"
            />
          </>
        )}

        {(payPalEnabled || props.paymentMethod === PaymentMethods.PayPal) && (
          <>
            {guestAccess &&
              creditCardEnabled && <Divider horizontal>Or</Divider>}
            <PayPal
              amount={props.balanceDue}
              estimateId={props.estimate ? props.estimate.estimateId : null}
              invoiceId={props.invoice ? props.invoice.invoiceId : null}
            />
          </>
        )}
      </div>
    </Segment>
  );
};

PaymentForm.defaultProps = {
  estimate: null,
  invoice: null,
};

PaymentForm.propTypes = {
  balanceDue: PropTypes.number.isRequired,
  estimate: PropTypes.shape({}),
  invoice: PropTypes.shape({}),
  router: PropTypes.shape({}).isRequired,
  paymentMethod: PropTypes.number,

  // functions
  errorAlert: PropTypes.func.isRequired,
  getPaymentProcessors: PropTypes.func.isRequired,
  loadCountries: PropTypes.func.isRequired,
  loadStates: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  router: state.router,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      errorAlert: alerts.error,
      getPaymentProcessors: processorActions.getPaymentProcessors,
      loadCountries: addressActions.loadCountries,
      loadStates: addressActions.loadStates,
      navigateTo: routerActions.navigateTo,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PaymentForm);



// WEBPACK FOOTER //
// ./src/components/Payments/PaymentCollection/components/PaymentForm/index.js